import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { where, orderBy, Timestamp, documentId } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Calendar,
  Clock,
  Loader2,
  PhoneOff,
  FileDown,
  RefreshCw,
  Star,
  Stethoscope,
  Timer,
  TrendingUp,
  UserCheck,
  UserX,
  Users,
  XCircle,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { InfoTooltip } from '../../components/ui/InfoTooltip';
import { useCollection } from '../../hooks/useFirestore';
import { useMedicosConLatido } from '../../hooks/useMedicosConLatido';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import type { Consulta, Sala, MedicoCompleto, EstadoConsulta, DisposicionConsulta } from '../../types';
import { TriageStatsSection } from '../../components/admin/TriageStatsSection';
import styles from './AdminDashboardPage.module.css';

// ── Cloud Function refs ─────────────────────────────────────

const obtenerMetricasDiaFn = httpsCallable(functions, 'portal_obtenerMetricasDia');

// ── Helpers ─────────────────────────────────────────────────

function todayPanama(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Panama',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function addDaysISO(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

function fmtFechaCorta(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

// ── Rango de fechas del dashboard ───────────────────────────
// Hoy / últimos 7 / últimos 30 / rango personalizado. Todas las métricas
// "del período" se calculan EN VIVO desde la colección `consultas` acotada
// por [desde, hasta]; lo instantáneo (cola ahora, médicos en línea,
// capacidad) no depende del rango.
type RangoDash = '1d' | '7d' | '30d' | 'custom';
const RANGOS_DASH: { key: RangoDash; label: string; dias: number }[] = [
  { key: '1d', label: 'Hoy', dias: 1 },
  { key: '7d', label: '7 días', dias: 7 },
  { key: '30d', label: '30 días', dias: 30 },
  { key: 'custom', label: 'Rango', dias: 7 },
];
const MAX_DIAS_RANGO_DASH = 92;

function diasEntreISO(desde: string, hasta: string): number {
  const [y1, m1, d1] = desde.split('-').map(Number);
  const [y2, m2, d2] = hasta.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000) + 1;
}

function fmtMin(min: number): string {
  if (min < 1) return '<1 min';
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function pct(part: number, total: number): string {
  if (total === 0) return '0%';
  return `${Math.round((part / total) * 100)}%`;
}

/** Truncate a name for chart axis labels */
function shortName(name: string, max = 14): string {
  if (name.length <= max) return name;
  return name.slice(0, max - 1) + '…';
}

// ── Estado labels & colors ──────────────────────────────────

const ESTADO_META: Record<EstadoConsulta, { label: string; color: string }> = {
  en_espera:   { label: 'En espera',    color: '#f59e0b' },
  asignada:    { label: 'Asignada',     color: '#6366f1' },
  en_atencion: { label: 'En atención',  color: '#3b82f6' },
  finalizada:  { label: 'Finalizada',   color: '#22c55e' },
  no_show:     { label: 'No show',      color: '#ef4444' },
  transferida: { label: 'Transferida',  color: '#8b5cf6' },
  cancelada:   { label: 'Cancelada',    color: '#6b7280' },
};

const DISPOSICION_META: Record<DisposicionConsulta, { label: string; color: string }> = {
  completada:           { label: 'Completada',     color: '#1A8048' },
  paciente_no_presento: { label: 'No se presentó', color: '#B42F2F' },
  paciente_rechazo:     { label: 'Rechazó llamada', color: '#dc2626' },
  fallo_tecnico:        { label: 'Fallo técnico',  color: '#B07700' },
  cancelada_medico:     { label: 'Cancelada médico', color: '#6b7280' },
  cancelada_supervisor: { label: 'Cancelada supervisor', color: '#6b7280' },
  cancelada_paciente:   { label: 'Cancelada paciente', color: '#9ca3af' },
  incompleta:           { label: 'Incompleta',     color: '#f97316' },
  referida:             { label: 'Referida',       color: '#8b5cf6' },
  tiempo_excedido:      { label: 'Tiempo agotado', color: '#0891b2' },
};

// Ventana de frescura del heartbeat para considerar a un médico "en línea" —
// constante ÚNICA compartida (antes duplicada en dos useMemo distintos, con
// riesgo real de que un cambio en una copia desincronizara la definición de
// "en línea" entre las tarjetas de capacidad y el panel de médicos).
const HEARTBEAT_MAX_MS = 120_000;

// Disposiciones que NO cuentan como consulta "efectiva" (no-show, fallo
// técnico, cancelaciones del médico, notas incompletas) — única definición
// compartida de "no atendida" en toda la página. `estado==='no_show'` se
// revisa también por compatibilidad, pero NINGÚN flujo del backend lo
// escribe hoy (el cron de no-show real marca `estado:'finalizada'` +
// `disposicion:'paciente_no_presento'`); confiar solo en `estado==='no_show'`
// hacía que "No shows" mostrara siempre 0.
const DISPOSICIONES_NO_EFECTIVAS = new Set<DisposicionConsulta>([
  'paciente_no_presento', 'fallo_tecnico', 'cancelada_medico', 'incompleta',
]);

function esConsultaNoAtendida(c: { estado: EstadoConsulta; disposicion?: DisposicionConsulta }): boolean {
  return c.estado === 'no_show'
    || (!!c.disposicion && DISPOSICIONES_NO_EFECTIVAS.has(c.disposicion));
}

// Umbrales de las "Alertas operacionales" (antes literales repetidos/partidos
// entre condiciones distintas del bloque de alertas).
const ALERTA_ESPERA_MAX_MIN = 30;
const ALERTA_COLA_SIN_MEDICOS_MIN = 5;

// ── Stars component ─────────────────────────────────────────

function Stars({ rating }: { rating: number }) {
  return (
    <span className={styles.ratingWrap}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={13}
          fill={n <= Math.round(rating) ? 'var(--brand-gold-500)' : 'none'}
          className={n <= Math.round(rating) ? styles.starFilled : styles.starEmpty}
        />
      ))}
    </span>
  );
}

// ── Custom Recharts tooltip ─────────────────────────────────

function ChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.chartTooltip}>
      {label && <p className={styles.chartTooltipLabel}>{label}</p>}
      {payload.map((entry) => (
        <p key={entry.name} className={styles.chartTooltipRow}>
          <span className={styles.legendDot} style={{ background: entry.color }} />
          {entry.name}: <strong>{entry.value}</strong>
        </p>
      ))}
    </div>
  );
}

// ── Donut + leyenda con barras de valor ─────────────────────
// Reemplaza los pies genéricos de recharts (labels de porcentaje encima +
// leyenda default) por un donut limpio con el total al centro y una leyenda
// tipo "tabla de valores": nombre · valor · % + barra proporcional.

interface DonutDatum { name: string; value: number; color: string }

function DonutPanel({ data, totalLabel }: { data: DonutDatum[]; totalLabel: string }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className={styles.donutRow}>
      <div className={styles.donutChart}>
        <ResponsiveContainer width="100%" height={216}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={64}
              outerRadius={94}
              paddingAngle={data.length > 1 ? 3 : 0}
              cornerRadius={5}
              stroke="none"
            >
              {data.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className={styles.donutCenter} aria-hidden="true">
          <span className={styles.donutTotal}>{total}</span>
          <span className={styles.donutTotalLbl}>{totalLabel}</span>
        </div>
      </div>

      <div className={styles.donutLegend}>
        {data.map((d) => {
          const p = total > 0 ? (d.value / total) * 100 : 0;
          return (
            <div key={d.name} className={styles.donutLegRow}>
              <span className={styles.donutLegDot} style={{ background: d.color }} />
              <span className={styles.donutLegName}>{d.name}</span>
              <span className={styles.donutLegVal}>{d.value}</span>
              <span className={styles.donutLegPct}>{Math.round(p)}%</span>
              <span className={styles.donutLegBar}>
                <span style={{ width: `${p}%`, background: d.color }} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── KPI aggregated from Cloud Function ──────────────────────

interface KpisDia {
  fecha: string;
  totalConsultas: number;
  porEstado: Record<string, number>;
  tiempoPromedioConsultaMin: number;
  tiempoPromedioEsperaMin: number;
  noShows: number;
  porcentajeNoShows: number;
  ratingPromedio: number | null;
  consultasCalificadas: number;
}

// ═════════════════════════════════════════════════════════════
//  AdminDashboardPage
// ═════════════════════════════════════════════════════════════

export default function AdminDashboardPage() {
  const [rango, setRango] = useState<RangoDash>('1d');
  const [customDesde, setCustomDesde] = useState(() => addDaysISO(todayPanama(), -6));
  const [customHasta, setCustomHasta] = useState(() => todayPanama());
  const [kpis, setKpis] = useState<KpisDia | null>(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Rango efectivo [desdeISO, hastaISO] (inclusive), normalizado.
  const { desdeISO, hastaISO } = useMemo(() => {
    const hoy = todayPanama();
    if (rango === 'custom') {
      let d = customDesde && customHasta && customDesde > customHasta ? customHasta : customDesde;
      let h = customDesde && customHasta && customDesde > customHasta ? customDesde : customHasta;
      if (!d || !h) return { desdeISO: addDaysISO(hoy, -6), hastaISO: hoy };
      if (h > hoy) h = hoy;
      if (d > h) d = h;
      if (diasEntreISO(d, h) > MAX_DIAS_RANGO_DASH) d = addDaysISO(h, -(MAX_DIAS_RANGO_DASH - 1));
      return { desdeISO: d, hastaISO: h };
    }
    const dias = RANGOS_DASH.find((r) => r.key === rango)?.dias ?? 1;
    return { desdeISO: addDaysISO(hoy, -(dias - 1)), hastaISO: hoy };
  }, [rango, customDesde, customHasta]);
  const esUnDia = desdeISO === hastaISO;

  // ── Rol: un médico ve SOLO sus propios indicadores ──────────
  // (admin/supervisor → soloMedicoId=null → dashboard global con todos los médicos)
  const { role, user } = useAuth();
  const soloMedicoId = role === 'medico' ? (user?.uid ?? null) : null;

  // ── Real-time collections ───────────────────────────────────

  // Cotas del rango en hora de Panamá (UTC-5). La cota SUPERIOR exclusiva
  // corrige además un bug previo: al elegir una fecha pasada la query solo
  // tenía `>= inicio` y arrastraba también todos los días posteriores.
  const rangoBounds = useMemo(() => ({
    start: Timestamp.fromDate(new Date(`${desdeISO}T00:00:00-05:00`)),
    endExclusive: Timestamp.fromDate(new Date(`${addDaysISO(hastaISO, 1)}T00:00:00-05:00`)),
  }), [desdeISO, hastaISO]);

  const consultasConstraints = useMemo(
    () => {
      const base = [
        where('creadoEn', '>=', rangoBounds.start),
        where('creadoEn', '<', rangoBounds.endExclusive),
        orderBy('creadoEn', 'desc'),
      ];
      // Médico: filtra a SUS consultas (las reglas sólo permiten leer las propias).
      return soloMedicoId ? [where('medicoId', '==', soloMedicoId), ...base] : base;
    },
    [rangoBounds, soloMedicoId],
  );

  const { data: consultas, loading: consultasLoading } = useCollection<Consulta>(
    'consultas',
    consultasConstraints,
  );

  const { data: salas } = useCollection<Sala>('salas');
  // Médico no puede listar /medicos (reglas) → restringir a su propio doc.
  const medicosConstraints = useMemo(
    () => (soloMedicoId ? [where(documentId(), '==', soloMedicoId)] : undefined),
    [soloMedicoId],
  );
  const { data: medicos } = useMedicosConLatido<MedicoCompleto>(medicosConstraints, soloMedicoId ?? undefined);

  // El uso/costo de IA tiene su propia página dedicada (/admin/uso-ia); ya no
  // se muestra un panel resumen en el dashboard.

  // Reloj para recalcular la frescura del heartbeat aunque no lleguen escrituras
  // (si todos los médicos se desconectan, no habría onSnapshot que refresque).
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    if (soloMedicoId) return;
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [soloMedicoId]);

  // ── Capacidad: se calcula EN VIVO desde la colección `medicos` (heartbeat) y
  //    la asignación de salas — MISMA fuente que "Estado de médicos". Antes se
  //    leía de los campos del cron (medicosEnTurno…), que sólo cuenta médicos
  //    que ya "entraron" a la sala (medicosActivos) con estado disponible; eso
  //    dejaba en 0 a un médico conectado y asignado que aún no entró a la cola.
  const capacidad = useMemo(() => {
    const porSala: Record<string, {
      medicosEnLinea: number; medicosLibres: number;
      cupoDinamico: number; capacidadPotencial: number; motivoSinCupo: string;
    }> = {};
    if (soloMedicoId) {
      return {
        cuposDinamicos: 0, capacidadPotencial: 0,
        medicosEnLinea: 0, medicosLibres: 0, medicosEnConsulta: 0, porSala, loaded: true,
      };
    }
    const HEARTBEAT_MAX_MS = 120_000;
    const activas = (salas ?? []).filter((s) => s.activa);
    const list = consultas ?? [];

    const estaBusy = (est?: string) =>
      est === 'en_consulta' || est === 'charting' || est === 'buffer';
    const estaPausa = (est?: string) => est === 'pausa' || est === 'break_almuerzo';
    const online = (m: MedicoCompleto) => {
      const hb = m.ultimoHeartbeat?.toMillis?.() ?? 0;
      return nowTick - hb < HEARTBEAT_MAX_MS;
    };
    const asignadoA = (m: MedicoCompleto, salaId: string) => {
      const sa = (m as { salasAsignadas?: string[] }).salasAsignadas ?? [];
      return sa.includes(salaId) || sa.includes('*');
    };

    const activos = (medicos ?? []).filter((m) => m.activo !== false);
    const onlineMedicos = activos.filter(online);

    // Agregados globales (médicos DISTINTOS, no por sala).
    const medicosEnLinea = onlineMedicos.length;
    const medicosEnConsulta = onlineMedicos.filter((m) => estaBusy(m.estado as string)).length;
    const medicosLibres = onlineMedicos.filter(
      (m) => !estaBusy(m.estado as string) && !estaPausa(m.estado as string),
    ).length;

    // Cupos por sala: médicos EN LÍNEA asignados a la sala → capacidad dinámica.
    let cuposDinamicos = 0, capacidadPotencial = 0;
    for (const s of activas) {
      const tConsulta = Number(
        s.tiempoConsultaDefault ?? s.tiempoConsultaMin ?? s.tiempoEstimadoMin ?? 15,
      ) || 15;
      const cuposPorMedicoHora = tConsulta > 0 ? Math.floor(60 / tConsulta) : 0;
      const horizonte = Number(s.horizonteCapacidadHoras ?? 3);
      const limite = Number(s.limitePacientes || 0);
      const factorOver = s.permiteOverbooking === true ? Number(s.factorOverbooking || 1) : 1;
      const limiteEfectivo = limite > 0 ? Math.floor(limite * factorOver) : 0;

      const asignadosOnline = onlineMedicos.filter((m) => asignadoA(m, s.id));
      // "En turno" para capacidad DINÁMICA (ahora): excluye a quien está en
      // pausa/almuerzo — decidió no tomar pacientes nuevos por un rato, así
      // que no debe contar como cupo disponible. NO excluye "busy"
      // (en_consulta/charting/buffer): esos médicos siguen produciendo
      // consultas dentro del horizonte de horas, solo están ocupados en este
      // instante. Bug real encontrado: antes `enTurno` contaba a TODOS los
      // médicos en línea sin filtrar pausa, así que pausar/despausar a un
      // médico no movía "Cupos disponibles ahora" en el dashboard.
      const enTurnoMedicos = asignadosOnline.filter((m) => !estaPausa(m.estado as string));
      const enTurno = enTurnoMedicos.length;
      const libresSala = asignadosOnline.filter(
        (m) => !estaBusy(m.estado as string) && !estaPausa(m.estado as string),
      ).length;
      const asignadosTotal = activos.filter((m) => asignadoA(m, s.id)).length;

      const capDin = enTurno * cuposPorMedicoHora * horizonte;
      const capPot = asignadosTotal * cuposPorMedicoHora * horizonte;
      const capDinEf = limiteEfectivo > 0 ? Math.min(capDin, limiteEfectivo) : capDin;
      const capPotEf = limiteEfectivo > 0 ? Math.min(capPot, limiteEfectivo) : capPot;

      const ocupado = list.filter(
        (c) => c.salaId === s.id && (c.estado === 'en_espera' || c.estado === 'en_atencion'),
      ).length;
      const cupoSala = Math.max(0, capDinEf - ocupado);

      cuposDinamicos += cupoSala;
      capacidadPotencial += capPotEf;
      porSala[s.id] = {
        medicosEnLinea: enTurno,
        medicosLibres: libresSala,
        cupoDinamico: cupoSala,
        capacidadPotencial: capPotEf,
        motivoSinCupo: enTurno === 0 ? 'sin_medicos' : cupoSala <= 0 ? 'tope' : 'ok',
      };
    }

    return {
      cuposDinamicos, capacidadPotencial,
      medicosEnLinea, medicosLibres, medicosEnConsulta, porSala, loaded: true,
    };
  }, [salas, medicos, consultas, nowTick, soloMedicoId]);

  // ── Fetch aggregated KPIs from Cloud Function ──────────────

  const fetchKpis = useCallback(async () => {
    // Médico: el CF de métricas es admin/supervisor only → usa cálculo local (live).
    // Rangos multi-día: el CF agrega UN día — se omite y todo sale del cálculo
    // en vivo (effectiveKpis ya hace fallback a `live`).
    if (soloMedicoId || !esUnDia) { setKpis(null); setKpisLoading(false); return; }
    setKpisLoading(true);
    try {
      const result = await obtenerMetricasDiaFn({ fecha: desdeISO });
      setKpis(result.data as KpisDia);
    } catch {
      setKpis(null);
    } finally {
      setKpisLoading(false);
    }
  }, [desdeISO, esUnDia, soloMedicoId]);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchKpis();
    setRefreshing(false);
  };

  // ── Exportar el dashboard (con el rango activo) a PDF ──────
  // Captura la página completa (todos los paneles, incluida la gráfica de
  // triage) como imagen y la pagina en A4. Las librerías (html-to-image +
  // jspdf, ~400KB) se cargan PEREZOSAMENTE al hacer clic para no engordar el
  // bundle inicial. Los controles del hero llevan data-no-export y quedan
  // fuera de la captura.
  const { addToast } = useToast();
  const pageRef = useRef<HTMLDivElement>(null);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const handleExportarPdf = useCallback(async () => {
    const nodo = pageRef.current;
    if (!nodo || exportandoPdf) return;
    setExportandoPdf(true);
    try {
      const [{ toPng }, { jsPDF }] = await Promise.all([
        import('html-to-image'),
        import('jspdf'),
      ]);
      const esOscuro = document.documentElement.dataset.theme === 'dark'
        || (!document.documentElement.dataset.theme && window.matchMedia('(prefers-color-scheme: dark)').matches);
      const fondo = esOscuro ? '#0d1512' : '#f8fafc';
      const dataUrl = await toPng(nodo, {
        pixelRatio: 2,
        backgroundColor: fondo,
        // Las hojas de Google Fonts son cross-origin: html-to-image no puede
        // leer sus cssRules (SecurityError) y la captura salía corrupta. Sin
        // embebido de fuentes la captura usa la tipografía de respaldo del
        // sistema — el layout y los datos se preservan.
        skipFonts: true,
        filter: (el) => !(el instanceof HTMLElement && el.dataset.noExport !== undefined),
      });
      const img = new Image();
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error('captura'));
        img.src = dataUrl;
      });

      // Paginación A4: la imagen (a lo ancho de la página) se "desliza" con
      // offset negativo por página; los márgenes sup/inf se tapan con bandas
      // del color de fondo y el pie lleva el período + fecha de generación.
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = 210, pageH = 297, margen = 8;
      const imgW = pageW - margen * 2;
      const imgH = (img.height * imgW) / img.width;
      const utilH = pageH - margen * 2;
      const paginas = Math.max(1, Math.ceil(imgH / utilH));
      const [fr, fg, fb] = esOscuro ? [13, 21, 18] : [248, 250, 252];
      const pie = `MINSA Teleconsulta · ${esUnDia ? desdeISO : `${desdeISO} → ${hastaISO}`} · generado ${new Date().toLocaleString('es-PA')}`;
      for (let p = 0; p < paginas; p++) {
        if (p > 0) pdf.addPage();
        pdf.setFillColor(fr, fg, fb);
        pdf.rect(0, 0, pageW, pageH, 'F');
        pdf.addImage(dataUrl, 'PNG', margen, margen - p * utilH, imgW, imgH, undefined, 'FAST');
        // Bandas que tapan el sangrado de la imagen en los márgenes.
        pdf.setFillColor(fr, fg, fb);
        pdf.rect(0, 0, pageW, margen, 'F');
        pdf.rect(0, pageH - margen, pageW, margen, 'F');
        pdf.setFontSize(7.5);
        pdf.setTextColor(esOscuro ? 160 : 100);
        pdf.text(pie, margen, pageH - 2.5);
        pdf.text(`Página ${p + 1} de ${paginas}`, pageW - margen, pageH - 2.5, { align: 'right' });
      }
      pdf.save(`dashboard_${desdeISO}${esUnDia ? '' : `_a_${hastaISO}`}.pdf`);
    } catch (err) {
      console.error('[Dashboard] exportar PDF:', err);
      addToast('error', 'No se pudo generar el PDF. Intente de nuevo.');
    } finally {
      setExportandoPdf(false);
    }
  }, [exportandoPdf, desdeISO, hastaISO, esUnDia, addToast]);

  // ── Local computed metrics (real-time from Firestore) ──────

  const live = useMemo(() => {
    const list = consultas ?? [];
    const total = list.length;

    // By status
    const porEstado: Record<string, number> = {};
    for (const c of list) {
      porEstado[c.estado] = (porEstado[c.estado] ?? 0) + 1;
    }

    const enEspera = porEstado['en_espera'] ?? 0;
    const enAtencion = porEstado['en_atencion'] ?? 0;
    const asignadas = porEstado['asignada'] ?? 0;
    const finalizadas = porEstado['finalizada'] ?? 0;
    // `estado==='no_show'` casi nunca se escribe — ver esConsultaNoAtendida.
    const noShows = list.filter(esConsultaNoAtendida).length;
    // Canceladas: TODAS las rutas de cancelación del backend escriben
    // estado 'finalizada' + disposicion 'cancelada_*' (deliberado, para que
    // queden en el historial — ver pacienteEntrarCola/gestionColas). Ningún
    // flujo escribe estado==='cancelada' en consultas hoy, así que contar
    // solo por estado dejaba este indicador clavado en 0 aunque el panel de
    // disposiciones sí mostrara cancelaciones.
    const canceladas = list.filter(
      (c) => c.estado === 'cancelada' || (c.disposicion ?? '').startsWith('cancelada_'),
    ).length;
    const transferidas = porEstado['transferida'] ?? 0;

    // Dispositions
    const porDisposicion: Record<string, number> = {};
    for (const c of list) {
      if (c.disposicion) {
        porDisposicion[c.disposicion] = (porDisposicion[c.disposicion] ?? 0) + 1;
      }
    }

    // Espera promedio HISTÓRICA del día (creadoEn → iniciadaEn de las
    // consultas que llegaron a ser atendidas) — MISMA definición que la
    // Cloud Function portal_obtenerMetricasDia (tiempoPromedioEsperaMin), para
    // que la tarjeta "Espera promedio" no cambie de significado según si la
    // CF respondió o no (antes el fallback medía algo distinto: el tiempo que
    // llevan esperando ahora mismo los pacientes que siguen en cola).
    const consultasIniciadas = list.filter((c) => c.iniciadaEn);
    let avgWaitMin = 0;
    if (consultasIniciadas.length > 0) {
      const waits = consultasIniciadas.map((c) => {
        const creado = c.creadoEn?.toMillis?.() ?? 0;
        const inicio = c.iniciadaEn!.toMillis?.() ?? 0;
        return (inicio - creado) / 60000;
      });
      avgWaitMin = waits.reduce((a, b) => a + b, 0) / waits.length;
    }

    // Espera MÁXIMA actual — de los pacientes que siguen en cola AHORA MISMO.
    // Esta sí es una métrica "en vivo" legítima, distinta de la histórica de
    // arriba (no tiene equivalente en la Cloud Function porque no aplica a un
    // día pasado).
    const now = Date.now();
    const waitingConsultas = list.filter((c) => c.estado === 'en_espera');
    let maxWaitMin = 0;
    if (waitingConsultas.length > 0) {
      maxWaitMin = Math.max(
        ...waitingConsultas.map((c) => (now - (c.creadoEn?.toMillis?.() ?? now)) / 60000),
      );
    }

    // Consultation duration (finalized)
    const finalized = list.filter((c) => c.estado === 'finalizada' && c.duracionSegundos);
    let avgDurMin = 0;
    if (finalized.length > 0) {
      const tot = finalized.reduce((s, c) => s + (c.duracionSegundos ?? 0), 0);
      avgDurMin = tot / finalized.length / 60;
    }

    // Ratings — calificación del PACIENTE (consulta.calificacion.estrellas,
    // escrita por portal_calificarConsulta), NO `calidadLlamada` (campo legado
    // autorreportado por el médico/sistema sobre la calidad de la llamada —
    // una métrica distinta que antes se mostraba bajo la misma etiqueta).
    const rated = list.filter((c) => c.calificacion?.estrellas);
    const avgRating = rated.length > 0
      ? rated.reduce((s, c) => s + (c.calificacion?.estrellas ?? 0), 0) / rated.length
      : null;

    // Per-sala — TODAS las salas activas (no sólo las que tienen consultas),
    // con los médicos EN LÍNEA + cupos calculados en vivo (capacidad.porSala).
    const salaStats = (salas ?? [])
      .filter((s) => s.activa)
      .map((sala) => {
        const cs = list.filter((c) => c.salaId === sala.id);
        const cap = capacidad.porSala[sala.id];
        return {
          id: sala.id,
          nombre: sala.nombre ?? sala.id,
          estado: sala.estado ?? 'cerrada',
          enEspera: cs.filter((c) => c.estado === 'en_espera').length,
          enAtencion: cs.filter((c) => c.estado === 'en_atencion').length,
          finalizadas: cs.filter((c) => c.estado === 'finalizada').length,
          noShows: cs.filter(esConsultaNoAtendida).length,
          medicosEnLinea: cap?.medicosEnLinea ?? 0,
          medicosLibres: cap?.medicosLibres ?? 0,
          cupoDinamico: cap?.cupoDinamico ?? 0,
          capacidadPotencial: cap?.capacidadPotencial ?? 0,
          motivoSinCupo: cap?.motivoSinCupo ?? 'ok',
        };
      })
      .sort((a, b) => (b.enEspera + b.enAtencion) - (a.enEspera + a.enAtencion));

    // Capacidad — calculada en vivo desde médicos (heartbeat) + asignación.
    const totalCuposDisponibles = capacidad.cuposDinamicos;   // según médicos en línea
    const totalCapacidad = capacidad.capacidadPotencial;       // potencial (todos en línea)
    // Pacientes en cola: MISMO conteo en vivo que `enEspera` (antes usaba la
    // suma de `sala.pacientesEnEspera`, un contador transaccional que el
    // propio backend documenta como propenso a desviarse — ver
    // tareasScheduled.ts, "corrige la DERIVA del contador" — y podía mostrar
    // un número distinto al del banner en vivo en esta misma pantalla).
    const totalPacientesEnEspera = enEspera;

    // Médicos — de los campos del cron (heartbeat fresco = en línea de verdad).
    const medicosOnline = capacidad.medicosEnLinea;                    // en turno / en línea
    const medicosDisponibles = capacidad.medicosLibres;               // libres ahora
    const medicosEnConsulta = capacidad.medicosEnConsulta;

    return {
      total, porEstado, enEspera, enAtencion, asignadas, finalizadas,
      noShows, canceladas, transferidas, porDisposicion,
      avgWaitMin, maxWaitMin, avgDurMin, avgRating, ratedCount: rated.length,
      salaStats,
      totalCuposDisponibles, totalCapacidad,
      totalPacientesEnEspera,
      medicosDisponibles, medicosEnConsulta, medicosOnline,
      // `!== false` (no `truthy`) trata a un médico sin el campo `activo`
      // como activo — misma convención que el resto de los filtros de
      // médicos en este archivo (antes este era el único que usaba un check
      // distinto, pudiendo desalinear el denominador de "médicos en línea").
      medicosTotal: (medicos ?? []).filter((m) => m.activo !== false).length,
    };
  }, [consultas, medicos, salas, capacidad]);

  // ── Panel unificado por médico ───────────────────────────────
  // Reemplaza los dos paneles previos ("Estado de médicos" y "Rendimiento
  // por médico"): antes mostraban números distintos para el mismo médico/día
  // porque usaban dos definiciones distintas de "atendida" (una contaba TODO
  // finalizada, la otra excluía no-shows/fallos vía `disposicion`). Ahora hay
  // una sola fuente: conexión REAL por heartbeat (no el `estado` stale),
  // actividad actual, y consultas del día (efectivas/no atendidas/en curso/
  // duración/calificación) con una única definición de "no atendida".
  const medicosPanel = useMemo(() => {
    if (soloMedicoId) return [];

    // Agregado de consultas del día por médico.
    const agg = new Map<string, {
      efectivas: number; noAtendidas: number; enCurso: number;
      duracionTotal: number; duracionCount: number;
      ratingSum: number; ratingCount: number;
    }>();
    for (const c of consultas ?? []) {
      if (!c.medicoId) continue;
      const a = agg.get(c.medicoId) ?? {
        efectivas: 0, noAtendidas: 0, enCurso: 0,
        duracionTotal: 0, duracionCount: 0, ratingSum: 0, ratingCount: 0,
      };
      if (esConsultaNoAtendida(c)) a.noAtendidas++;
      else if (c.estado === 'finalizada') a.efectivas++;
      else if (c.estado === 'cancelada') a.noAtendidas++;
      if (c.estado === 'en_atencion') a.enCurso++;
      if (c.duracionSegundos) {
        a.duracionTotal += c.duracionSegundos;
        a.duracionCount++;
      }
      if (c.calificacion?.estrellas) {
        a.ratingSum += c.calificacion.estrellas;
        a.ratingCount++;
      }
      agg.set(c.medicoId, a);
    }

    type Conexion = 'conectado' | 'pausa' | 'desconectado';
    type Actividad = 'atendiendo' | 'en_espera' | null;

    const rows = (medicos ?? [])
      .filter((m) => m.activo !== false)
      .map((m) => {
        const est = m.estado as string | undefined;
        const hb = m.ultimoHeartbeat?.toMillis?.() ?? 0;
        const fresh = nowTick - hb < HEARTBEAT_MAX_MS;
        let conexion: Conexion;
        if (!fresh) conexion = 'desconectado';
        else if (est === 'pausa' || est === 'break_almuerzo') conexion = 'pausa';
        else conexion = 'conectado';

        let actividad: Actividad = null;
        if (conexion === 'conectado') {
          actividad =
            est === 'en_consulta' || est === 'charting' || est === 'buffer'
              ? 'atendiendo'
              : 'en_espera';
        }

        const a = agg.get(m.id) ?? {
          efectivas: 0, noAtendidas: 0, enCurso: 0,
          duracionTotal: 0, duracionCount: 0, ratingSum: 0, ratingCount: 0,
        };
        return {
          id: m.id,
          nombre: m.nombreCompleto ?? 'Médico',
          especialidad: m.especialidad ?? '',
          fotoURL: m.avatarUrl ?? null,
          conexion,
          actividad,
          efectivas: a.efectivas,
          noAtendidas: a.noAtendidas,
          enCurso: a.enCurso,
          duracionProm: a.duracionCount > 0 ? a.duracionTotal / a.duracionCount / 60 : null,
          calificacionProm: a.ratingCount > 0 ? a.ratingSum / a.ratingCount : null,
        };
      });

    // Orden: en línea primero (conectado→pausa→desconectado), luego más efectivas.
    const rank: Record<Conexion, number> = { conectado: 0, pausa: 1, desconectado: 2 };
    rows.sort((x, y) => rank[x.conexion] - rank[y.conexion] || y.efectivas - x.efectivas);
    return rows;
  }, [medicos, consultas, nowTick, soloMedicoId]);

  // ── Use CF data when available, fallback to local ──────────

  const effectiveKpis = {
    total: kpis?.totalConsultas ?? live.total,
    avgWaitMin: kpis?.tiempoPromedioEsperaMin ?? live.avgWaitMin,
    avgDurMin: kpis?.tiempoPromedioConsultaMin ?? live.avgDurMin,
    noShows: kpis?.noShows ?? live.noShows,
    pctNoShows: kpis?.porcentajeNoShows ?? (live.total > 0 ? Math.round((live.noShows / live.total) * 100) : 0),
    avgRating: kpis?.ratingPromedio ?? live.avgRating,
    ratedCount: kpis?.consultasCalificadas ?? live.ratedCount,
  };

  // ── Chart data ─────────────────────────────────────────────

  const estadoPieData = useMemo(() =>
    (Object.keys(ESTADO_META) as EstadoConsulta[])
      .map((key) => ({
        name: ESTADO_META[key].label,
        value: live.porEstado[key] ?? 0,
        color: ESTADO_META[key].color,
      }))
      .filter((d) => d.value > 0),
    [live.porEstado],
  );

  const disposicionPieData = useMemo(() =>
    (Object.keys(DISPOSICION_META) as DisposicionConsulta[])
      .map((key) => ({
        name: DISPOSICION_META[key].label,
        value: live.porDisposicion[key] ?? 0,
        color: DISPOSICION_META[key].color,
      }))
      .filter((d) => d.value > 0),
    [live.porDisposicion],
  );

  // Top 10 médicos con actividad hoy, por consultas efectivas — misma
  // definición de "efectivas"/"no atendidas" que la tabla unificada de abajo.
  const doctorBarData = useMemo(() =>
    medicosPanel
      .filter((d) => d.efectivas + d.enCurso + d.noAtendidas > 0)
      .slice()
      .sort((a, b) => b.efectivas - a.efectivas)
      .slice(0, 10)
      .map((d) => ({
        name: shortName(d.nombre),
        Efectivas: d.efectivas,
        'En curso': d.enCurso,
        'No atendidas': d.noAtendidas,
      })),
    [medicosPanel],
  );

  const salaBarData = useMemo(() =>
    live.salaStats.map((s) => ({
      name: shortName(s.nombre),
      'En espera': s.enEspera,
      'En atención': s.enAtencion,
      Finalizadas: s.finalizadas,
      'No show': s.noShows,
    })),
    [live.salaStats],
  );

  const isLoading = consultasLoading && kpisLoading;
  // "Hoy" (rango de un día que termina hoy) → banner y métricas en vivo.
  const isToday = esUnDia && hastaISO === todayPanama();

  if (isLoading) {
    return (
      <MainLayout pageTitle="Dashboard">
        <div className={styles.loadingWrap}>
          <Loader2 size={32} />
          <span>Cargando métricas...</span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout pageTitle="Dashboard">
      <div className={styles.page} ref={pageRef}>
        {/* ─── Hero: banda de mando con stats en vivo ──── */}
        <div className={styles.hero}>
          <div className={styles.heroTop}>
            <div className={styles.heroTitleWrap}>
              <span className={styles.heroKicker}>
                {isToday && <span className={styles.liveDot} aria-hidden="true" />}
                {isToday
                  ? 'Operación en vivo'
                  : esUnDia
                    ? `Histórico · ${desdeISO}`
                    : `Del ${fmtFechaCorta(desdeISO)} al ${fmtFechaCorta(hastaISO)} · ${diasEntreISO(desdeISO, hastaISO)} días`}
              </span>
              <h1 className={styles.heroTitle}>{soloMedicoId ? 'Mi Dashboard' : 'Dashboard Operacional'}</h1>
              <p className={styles.heroSubtitle}>
                {soloMedicoId ? 'Tus indicadores de atención del día' : 'Indicadores de atención en tiempo real'}
              </p>
            </div>

            <div className={styles.heroActions} data-no-export="">
              <div className={styles.heroRange} role="tablist" aria-label="Rango de fechas">
                {RANGOS_DASH.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    role="tab"
                    aria-selected={rango === r.key}
                    className={`${styles.heroRangeBtn} ${rango === r.key ? styles.heroRangeBtnActive : ''}`}
                    onClick={() => setRango(r.key)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {rango === 'custom' && (
                <>
                  <label className={`${styles.dateSelector} ${styles.heroCtl}`} title="Desde">
                    <Calendar size={14} />
                    <input
                      type="date"
                      className={styles.dateInput}
                      value={customDesde}
                      max={todayPanama()}
                      onChange={(e) => setCustomDesde(e.target.value)}
                      aria-label="Desde"
                    />
                  </label>
                  <label className={`${styles.dateSelector} ${styles.heroCtl}`} title="Hasta">
                    <Calendar size={14} />
                    <input
                      type="date"
                      className={styles.dateInput}
                      value={customHasta}
                      max={todayPanama()}
                      onChange={(e) => setCustomHasta(e.target.value)}
                      aria-label="Hasta"
                    />
                  </label>
                </>
              )}
              <button
                type="button"
                className={`${styles.refreshBtn} ${styles.heroCtl}`}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw size={14} className={refreshing ? styles.spinning : ''} />
                Actualizar
              </button>
              <button
                type="button"
                className={`${styles.refreshBtn} ${styles.heroCtl}`}
                onClick={handleExportarPdf}
                disabled={exportandoPdf}
                title="Exportar el dashboard (período actual) a PDF"
              >
                {exportandoPdf
                  ? <Loader2 size={14} className={styles.spinning} />
                  : <FileDown size={14} />}
                {exportandoPdf ? 'Generando…' : 'Exportar PDF'}
              </button>
            </div>
          </div>

          {isToday && !soloMedicoId && (
            <div className={styles.heroStats}>
              <div className={styles.heroStat}>
                <span className={styles.heroStatNum}>{live.enEspera}</span>
                <span className={styles.heroStatLbl}>Pacientes en cola</span>
              </div>
              <div className={styles.heroStat}>
                <span className={styles.heroStatNum}>{live.enAtencion}</span>
                <span className={styles.heroStatLbl}>En atención</span>
              </div>
              <div className={styles.heroStat}>
                <span className={styles.heroStatNum}>{live.medicosOnline}</span>
                <span className={styles.heroStatLbl}>Médicos en línea</span>
              </div>
              <div className={styles.heroStat}>
                <span className={styles.heroStatNum}>{live.totalCuposDisponibles}</span>
                <span className={styles.heroStatLbl}>Cupos disponibles ahora</span>
              </div>
            </div>
          )}
        </div>

        {/* ─── KPI Cards ──────────────────────────────── */}
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: 'var(--color-primary)' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                {esUnDia ? 'Consultas del día' : 'Consultas del período'}
                <InfoTooltip text="Total de teleconsultas creadas en el período seleccionado, en cualquier estado (en espera, en atención, finalizadas, canceladas). Sirve para ver el volumen total de demanda." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'var(--color-primary-soft)', color: 'var(--color-primary)' }}>
                <Stethoscope size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>{effectiveKpis.total}</p>
            <p className={styles.kpiMeta}>
              {live.finalizadas} finalizadas &middot; {live.enAtencion} en curso
            </p>
          </div>

          {/* Métricas de cola/sistema (no propias del médico) → solo admin */}
          {!soloMedicoId && (<>
          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: '#f59e0b' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                En espera ahora
                <InfoTooltip text="Pacientes con consulta en estado 'en_espera' en este momento (aún no tomados por ningún médico). Es un conteo en vivo, no histórico del día. Objetivo: ver la cola actual." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}>
                <Users size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>{live.enEspera}</p>
            <p className={styles.kpiMeta}>
              {live.asignadas} asignadas pendientes
            </p>
          </div>

          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: '#6366f1' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                Espera promedio
                <InfoTooltip text="Promedio del tiempo entre que el paciente solicitó la cita y un médico la tomó, para las consultas del día que llegaron a iniciarse. 'Máx' es la espera actual más larga entre quienes siguen en cola ahora. Objetivo: medir qué tan rápido se atiende la demanda." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
                <Clock size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>{fmtMin(effectiveKpis.avgWaitMin)}</p>
            <p className={styles.kpiMeta}>
              Máx: {fmtMin(live.maxWaitMin)}
            </p>
          </div>
          </>)}

          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: '#3b82f6' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                Duración promedio
                <InfoTooltip text="Promedio de duración real de las videollamadas finalizadas hoy (desde que inicia hasta que termina la consulta). Objetivo: dimensionar cuánto tiempo toma cada consulta en la práctica." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'var(--color-info-bg)', color: 'var(--color-info)' }}>
                <Timer size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>{fmtMin(effectiveKpis.avgDurMin)}</p>
            <p className={styles.kpiMeta}>
              {live.finalizadas} consultas finalizadas
            </p>
          </div>

          {!soloMedicoId && (
          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: '#22c55e' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                Médicos en línea
                <InfoTooltip text="Médicos activos con latido de conexión (heartbeat) de menos de 2 minutos — es decir, con la app realmente abierta ahora, no solo con la sesión iniciada. 'Libres' están disponibles para tomar pacientes; 'en consulta' están atendiendo o completando notas (no incluye a quienes están en pausa)." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'var(--color-success-bg)', color: 'var(--color-success)' }}>
                <UserCheck size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>{live.medicosOnline}</p>
            <p className={styles.kpiMeta}>
              {live.medicosDisponibles} libres &middot; {live.medicosEnConsulta} en consulta
            </p>
          </div>
          )}

          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: '#ef4444' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                No shows
                <InfoTooltip text="Consultas del día donde el paciente no se presentó a la videollamada (no entró a la sala o se marcó 'paciente_no_presento'). El % es sobre el total de consultas del día. Objetivo: medir ausentismo de pacientes." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'var(--color-error-bg)', color: 'var(--color-error)' }}>
                <UserX size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>{effectiveKpis.noShows}</p>
            <p className={styles.kpiMeta}>
              {effectiveKpis.pctNoShows}% del total
            </p>
          </div>

          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: '#6b7280' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                Canceladas
                <InfoTooltip text="Consultas del día canceladas por el paciente, un médico o un supervisor antes de completarse. 'Transferidas' son las que un médico movió a otra sala/médico en vez de cancelarlas." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'var(--color-surface-muted)', color: 'var(--color-text-tertiary)' }}>
                <XCircle size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>{live.canceladas}</p>
            <p className={styles.kpiMeta}>
              {live.transferidas} transferidas
            </p>
          </div>

          <div className={styles.kpiCard}>
            <div className={styles.kpiCardAccent} style={{ background: 'var(--brand-gold-500)' }} />
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>
                Calificación
                <InfoTooltip text="Promedio de estrellas (1-5) que los pacientes le dieron a su consulta al finalizar, sobre las consultas del día que fueron calificadas. No incluye la calidad de la llamada auto-reportada por el sistema." />
              </p>
              <div className={styles.kpiIcon} style={{ background: 'var(--color-gold-soft)', color: 'var(--color-gold-deep)' }}>
                <Star size={16} />
              </div>
            </div>
            <p className={styles.kpiValue}>
              {effectiveKpis.avgRating != null ? effectiveKpis.avgRating.toFixed(1) : '—'}
            </p>
            <p className={styles.kpiMeta}>
              {effectiveKpis.ratedCount} calificaciones
              {effectiveKpis.avgRating != null && (
                <> &middot; <Stars rating={effectiveKpis.avgRating} /></>
              )}
            </p>
          </div>
        </div>

        {/* ─── Charts: Estado Pie + Disposición Pie ──── */}
        <div className={styles.twoCol}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <Activity size={16} /> Distribución por estado
              </h2>
              <span className={styles.sectionBadge}>{live.total} total</span>
            </div>
            <div className={styles.sectionBody}>
              {estadoPieData.length > 0 ? (
                <DonutPanel data={estadoPieData} totalLabel="consultas" />
              ) : (
                <p className={styles.emptyState}>Sin consultas registradas</p>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <TrendingUp size={16} /> Disposición de finalizadas
              </h2>
              <span className={styles.sectionBadge}>{live.finalizadas} finalizadas</span>
            </div>
            <div className={styles.sectionBody}>
              {disposicionPieData.length > 0 ? (
                <DonutPanel data={disposicionPieData} totalLabel="finalizadas" />
              ) : (
                <p className={styles.emptyState}>Sin consultas finalizadas aún</p>
              )}
            </div>
          </div>
        </div>


        {/* ─── Chart: Consultas por médico (comparativa → solo admin) ─── */}
        {!soloMedicoId && doctorBarData.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <Stethoscope size={16} /> Consultas por médico
              </h2>
              <span className={styles.sectionBadge}>Top {doctorBarData.length}</span>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.chartWrap}>
                <ResponsiveContainer width="100%" height={Math.max(280, doctorBarData.length * 44)}>
                  <BarChart
                    data={doctorBarData}
                    layout="vertical"
                    margin={{ left: 10, right: 20, top: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--color-border-soft)" />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'var(--color-text-tertiary)' }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--color-surface-muted)' }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Efectivas" fill="#22c55e" barSize={11} radius={[0, 6, 6, 0]} />
                    <Bar dataKey="En curso" fill="#3b82f6" barSize={11} radius={[0, 6, 6, 0]} />
                    <Bar dataKey="No atendidas" fill="#ef4444" barSize={11} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* ─── Chart: Actividad por sala (solo admin) ──── */}
        {!soloMedicoId && salaBarData.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <BarChart3 size={16} /> Actividad por sala
              </h2>
              <span className={styles.sectionBadge}>{salaBarData.length} salas</span>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.chartWrap}>
                <ResponsiveContainer width="100%" height={Math.max(280, salaBarData.length * 52)}>
                  <BarChart
                    data={salaBarData}
                    layout="vertical"
                    margin={{ left: 10, right: 20, top: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--color-border-soft)" />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'var(--color-text-tertiary)' }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--color-surface-muted)' }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="En espera" fill="#f59e0b" barSize={11} radius={[0, 6, 6, 0]} />
                    <Bar dataKey="En atención" fill="#3b82f6" barSize={11} radius={[0, 6, 6, 0]} />
                    <Bar dataKey="Finalizadas" fill="#22c55e" barSize={11} radius={[0, 6, 6, 0]} />
                    <Bar dataKey="No show" fill="#ef4444" barSize={11} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* ─── Bloque global (capacidad + comparativas por médico/sala) → solo admin ─── */}
        {!soloMedicoId && (<>
        {/* ─── Capacity + Alerts ──────────────────────── */}
        <div className={styles.twoCol}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <BarChart3 size={16} /> Capacidad del sistema
              </h2>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.statusGrid}>
                <div className={styles.statusItem}>
                  <div className={styles.statusDot} style={{ background: 'var(--color-success)' }} />
                  <div className={styles.statusInfo}>
                    <p className={styles.statusLabel}>
                      Cupos disponibles ahora
                      <InfoTooltip text="Estimado de pacientes que se pueden atender en las próximas horas (horizonte configurable por sala), según los médicos en línea que NO están en pausa/almuerzo, menos los que ya están en cola o en atención. 'Potencial' asume que TODOS los médicos asignados (en línea o no) estuvieran disponibles — es el techo máximo, no lo real ahora." />
                    </p>
                    <p className={styles.statusValue}>
                      {live.medicosOnline === 0 ? (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>
                          Sin médicos en línea
                        </span>
                      ) : (
                        <>
                          {live.totalCuposDisponibles}
                          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>
                            {' '}de {live.totalCapacidad} potencial
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className={styles.statusItem}>
                  <div className={styles.statusDot} style={{ background: '#f59e0b' }} />
                  <div className={styles.statusInfo}>
                    <p className={styles.statusLabel}>
                      Pacientes en cola
                      <InfoTooltip text="Pacientes con consulta en estado 'en_espera' en este momento, sumados de todas las salas — el mismo número que 'En espera ahora' arriba." />
                    </p>
                    <p className={styles.statusValue}>{live.totalPacientesEnEspera}</p>
                  </div>
                </div>
                <div className={styles.statusItem}>
                  <div className={styles.statusDot} style={{ background: '#3b82f6' }} />
                  <div className={styles.statusInfo}>
                    <p className={styles.statusLabel}>
                      Médicos en línea
                      <InfoTooltip text="Médicos con latido de conexión (heartbeat) de menos de 2 minutos, sobre el total de médicos activos en el sistema." />
                    </p>
                    <p className={styles.statusValue}>{live.medicosOnline}/{live.medicosTotal}</p>
                  </div>
                </div>
                <div className={styles.statusItem}>
                  <div className={styles.statusDot} style={{ background: '#22c55e' }} />
                  <div className={styles.statusInfo}>
                    <p className={styles.statusLabel}>
                      Médicos libres ahora
                      <InfoTooltip text="De los médicos en línea, cuántos NO están atendiendo una consulta, completando notas ni en pausa/almuerzo — es decir, listos para recibir un paciente nuevo en este instante." />
                    </p>
                    <p className={styles.statusValue}>{live.medicosDisponibles}</p>
                  </div>
                </div>
              </div>

              {live.medicosTotal > 0 && (
                <div className={styles.progressWrap} style={{ marginTop: 'var(--space-2)' }}>
                  <div className={styles.progressLabel}>
                    <span>
                      Ocupación médica
                      <InfoTooltip text="Porcentaje de los médicos en línea que están actualmente en consulta o completando notas (no incluye pausa). Objetivo: ver qué tan saturado está el equipo médico ahora mismo." />
                    </span>
                    <span>{pct(live.medicosEnConsulta, live.medicosOnline || 1)}</span>
                  </div>
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{
                        width: `${live.medicosOnline > 0 ? (live.medicosEnConsulta / live.medicosOnline) * 100 : 0}%`,
                        background: 'var(--color-primary)',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <AlertTriangle size={16} /> Alertas operacionales
              </h2>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.statusGrid}>
                {live.maxWaitMin > ALERTA_ESPERA_MAX_MIN && (
                  <div className={styles.statusItem}>
                    <div className={styles.statusDot} style={{ background: 'var(--color-error)' }} />
                    <div className={styles.statusInfo}>
                      <p className={styles.statusLabel}>Espera máxima alta</p>
                      <p className={styles.statusValue}>{fmtMin(live.maxWaitMin)}</p>
                    </div>
                  </div>
                )}
                {live.noShows > 0 && (
                  <div className={styles.statusItem}>
                    <div className={styles.statusDot} style={{ background: 'var(--color-error)' }} />
                    <div className={styles.statusInfo}>
                      <p className={styles.statusLabel}>{esUnDia ? 'No shows hoy' : 'No shows en el período'}</p>
                      <p className={styles.statusValue}>{live.noShows}</p>
                    </div>
                  </div>
                )}
                {live.canceladas > 0 && (
                  <div className={styles.statusItem}>
                    <div className={styles.statusDot} style={{ background: 'var(--color-warning)' }} />
                    <div className={styles.statusInfo}>
                      <p className={styles.statusLabel}>Cancelaciones</p>
                      <p className={styles.statusValue}>{live.canceladas}</p>
                    </div>
                  </div>
                )}
                {live.enEspera > ALERTA_COLA_SIN_MEDICOS_MIN && live.medicosDisponibles === 0 && (
                  <div className={styles.statusItem}>
                    <div className={styles.statusDot} style={{ background: 'var(--color-error)' }} />
                    <div className={styles.statusInfo}>
                      <p className={styles.statusLabel}>Sin médicos disponibles</p>
                      <p className={styles.statusValue}>{live.enEspera} esperando</p>
                    </div>
                  </div>
                )}
                {live.maxWaitMin <= ALERTA_ESPERA_MAX_MIN && live.noShows === 0 && live.canceladas === 0 && (
                  <div className={styles.statusItem}>
                    <div className={styles.statusDot} style={{ background: 'var(--color-success)' }} />
                    <div className={styles.statusInfo}>
                      <p className={styles.statusLabel}>Estado general</p>
                      <p className={styles.statusValue} style={{ fontSize: 'var(--font-size-base)' }}>
                        Sin alertas activas
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Per-Sala Cards ─────────────────────────── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <PhoneOff size={16} /> Estado por sala
            </h2>
            <span className={styles.sectionBadge}>
              {live.salaStats.length} salas activas
            </span>
          </div>
          <div className={styles.sectionBody}>
            {live.salaStats.length > 0 ? (
              <div className={styles.salaGrid}>
                {live.salaStats.map((sala) => {
                  const estadoSala = sala.estado;
                  const badgeVariant =
                    estadoSala === 'abierta' ? 'success' as const :
                    estadoSala === 'pausa' ? 'warning' as const :
                    estadoSala === 'emergencia' ? 'error' as const :
                    'neutral' as const;

                  const capPct = sala.capacidadPotencial > 0
                    ? Math.min(100, Math.round((sala.cupoDinamico / sala.capacidadPotencial) * 100))
                    : 0;

                  return (
                    <div key={sala.id} className={styles.salaCard} data-estado={estadoSala || 'cerrada'}>
                      <div className={styles.salaCardHeader}>
                        <p className={styles.salaName}>{sala.nombre}</p>
                        <Badge variant={badgeVariant}>
                          {estadoSala || 'cerrada'}
                        </Badge>
                      </div>

                      {/* Métricas del día */}
                      <div className={styles.salaMetricStrip}>
                        <div className={styles.salaMetric}>
                          <span className={styles.salaMetricVal}>{sala.enEspera}</span>
                          <span className={styles.salaMetricLbl}>En espera</span>
                        </div>
                        <div className={styles.salaMetric}>
                          <span className={styles.salaMetricVal}>{sala.enAtencion}</span>
                          <span className={styles.salaMetricLbl}>En atención</span>
                        </div>
                        <div className={styles.salaMetric}>
                          <span className={styles.salaMetricVal}>{sala.finalizadas}</span>
                          <span className={styles.salaMetricLbl}>Finalizadas</span>
                        </div>
                        <div className={styles.salaMetric}>
                          <span className={`${styles.salaMetricVal} ${sala.noShows > 0 ? styles.salaMetricWarn : ''}`}>{sala.noShows}</span>
                          <span className={styles.salaMetricLbl}>No shows</span>
                        </div>
                      </div>

                      {/* Capacidad */}
                      <div className={styles.salaCapBlock}>
                        <div className={styles.salaCapRow}>
                          <span className={styles.salaCapLbl}>
                            <UserCheck size={13} aria-hidden="true" /> Médicos en línea / libres
                            <InfoTooltip text="En línea: médicos asignados a esta sala con conexión activa (heartbeat < 2 min). Libres: de esos, cuántos no están en consulta, notas ni pausa/almuerzo ahora mismo." />
                          </span>
                          <span className={styles.salaCapVal}>{sala.medicosEnLinea} / {sala.medicosLibres}</span>
                        </div>
                        <div className={styles.salaCapRow}>
                          <span className={styles.salaCapLbl}>
                            Cupos disponibles ahora
                            <InfoTooltip text="Pacientes que esta sala puede atender en las próximas horas según los médicos en línea que no están en pausa, menos los que ya están en cola o en atención. También respeta el límite de pacientes configurado, si lo hay." />
                          </span>
                          <span className={styles.salaCapVal}>
                            {sala.cupoDinamico}
                            <span className={styles.salaCapMuted}> / {sala.capacidadPotencial}</span>
                          </span>
                        </div>
                        <div className={styles.salaCapBar} title={`${sala.cupoDinamico} de ${sala.capacidadPotencial} cupos disponibles`}>
                          <span
                            className={styles.salaCapBarFill}
                            style={{ width: `${capPct}%`, background: capPct === 0 ? 'var(--color-text-tertiary)' : undefined }}
                          />
                        </div>
                      </div>

                      {sala.medicosEnLinea === 0 && (
                        <p className={styles.salaWarn}>Sin médicos en línea — 0 cupos disponibles</p>
                      )}
                      {sala.medicosEnLinea > 0 && sala.motivoSinCupo === 'tope' && (
                        <p className={styles.salaWarn}>Sala saturada — sin cupos hasta liberar espacio</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={styles.emptyState}>Sin actividad en salas</p>
            )}
          </div>
        </div>

        {/* ─── Triage: clasificación de pacientes (solo admin/supervisor) ───
             Usa el MISMO rango del selector global del dashboard. */}
        {!soloMedicoId && <TriageStatsSection desde={desdeISO} hasta={hastaISO} />}

        {/* ─── Estado y rendimiento de médicos (solo admin/supervisor) ───
             Panel único: antes esto eran dos tablas separadas ("Estado de
             médicos" y "Rendimiento por médico") que mostraban conteos
             distintos de "atendidas" para el mismo médico/día — ver
             DISPOSICIONES_NO_EFECTIVAS arriba. */}
        {!soloMedicoId && medicosPanel.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <Stethoscope size={16} /> Estado y rendimiento de médicos
              </h2>
              <span className={styles.sectionBadge}>
                {medicosPanel.filter((m) => m.conexion !== 'desconectado').length} en línea · {medicosPanel.length} total
              </span>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Médico</th>
                      <th>Conexión</th>
                      <th>Actividad</th>
                      <th style={{ textAlign: 'right' }}>Efectivas</th>
                      <th style={{ textAlign: 'right' }}>No atendidas</th>
                      <th style={{ textAlign: 'right' }}>En curso</th>
                      <th style={{ textAlign: 'right' }}>T. promedio</th>
                      <th style={{ textAlign: 'right' }}>Calificación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicosPanel.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <div className={styles.medicoCell}>
                            <Avatar
                              className={styles.medicoAvatar}
                              nombre={m.nombre}
                              fotoURL={m.fotoURL}
                            />
                            <div>
                              <div className={styles.medicoNombre}>{m.nombre}</div>
                              {m.especialidad && (
                                <div className={styles.medicoEsp}>{m.especialidad}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          {m.conexion === 'conectado' ? (
                            <Badge variant="success" size="sm">Conectado</Badge>
                          ) : m.conexion === 'pausa' ? (
                            <Badge variant="warning" size="sm">En pausa</Badge>
                          ) : (
                            <Badge variant="neutral" size="sm">Desconectado</Badge>
                          )}
                        </td>
                        <td>
                          {m.actividad === 'atendiendo' ? (
                            <Badge variant="info" size="sm">Atendiendo</Badge>
                          ) : m.actividad === 'en_espera' ? (
                            <Badge variant="neutral" size="sm">En espera</Badge>
                          ) : (
                            <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--color-success)' }}>
                          {m.efectivas}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: m.noAtendidas > 0 ? 'var(--color-error)' : 'var(--color-text-tertiary)' }}>
                          {m.noAtendidas}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {m.enCurso > 0 ? <Badge variant="info">{m.enCurso}</Badge> : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {m.duracionProm != null ? fmtMin(m.duracionProm) : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {m.calificacionProm != null ? (
                            <span className={styles.ratingWrap}>
                              {m.calificacionProm.toFixed(1)} <Stars rating={m.calificacionProm} />
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', margin: 'var(--space-2) 0 0' }}>
                Conexión por latido de presencia (últimos 2 min). «No atendidas» = no-show, fallo técnico, cancelaciones o notas incompletas del día. «Calificación» = promedio que dieron los pacientes a sus consultas finalizadas.
              </p>
            </div>
          </div>
        )}
        </>)}
      </div>
    </MainLayout>
  );
}
